import React, { useEffect, useRef, useState } from 'react'
import { accountBackupsAPI } from '@hpanel/web-common/api/client'
import type { RestoreJobV3 } from '@hpanel/web-common/api/client'
import { pollWithBackoff } from './pollWithBackoff'

interface Props {
  restoreId: number
  onFinished?: (job: RestoreJobV3) => void
}

const TERMINAL = new Set(['success', 'partial', 'failed'])

/**
 * Polls GET /api/backups/restores/{id} until success | partial | failed and
 * renders the outcome plus report.errors. Server error text is shown verbatim.
 */
export const RestoreJobResult: React.FC<Props> = ({ restoreId, onFinished }) => {
  const [job, setJob] = useState<RestoreJobV3 | null>(null)
  const [pollError, setPollError] = useState<string | null>(null)
  const signalRef = useRef({ cancelled: false })

  useEffect(() => {
    const signal = signalRef.current
    pollWithBackoff<RestoreJobV3>(
      () => accountBackupsAPI.restoreStatus(restoreId),
      (r) => TERMINAL.has(r.status),
      (r) => setJob(r),
      signal,
    )
      .then((r) => {
        if (!signal.cancelled) onFinished?.(r)
      })
      .catch((e: unknown) => {
        if (signal.cancelled) return
        console.error('RestoreJobResult: polling failed', e)
        setPollError(e instanceof Error ? e.message : 'Restore polling failed')
      })
    return () => {
      signal.cancelled = true
    }
  }, [restoreId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (pollError) {
    return (
      <div data-testid="restore-poll-error" role="alert" className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded-lg p-3 text-sm">
        {pollError}
      </div>
    )
  }
  if (!job || !TERMINAL.has(job.status)) {
    return (
      <div data-testid="backups-restore-progress" className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
        <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
        Restoring… this may take a few minutes.
      </div>
    )
  }
  const errors = job.report?.errors ?? []
  const tone =
    job.status === 'success'
      ? 'bg-green-50 dark:bg-green-900/30 border-green-300 text-green-800 dark:text-green-200'
      : job.status === 'partial'
        ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-300 text-yellow-800 dark:text-yellow-200'
        : 'bg-red-50 dark:bg-red-900/30 border-red-300 text-red-800 dark:text-red-200'
  const label =
    job.status === 'success'
      ? 'Restore completed'
      : job.status === 'partial'
        ? 'Restore completed with errors'
        : 'Restore failed'
  return (
    <div data-testid="restore-result" data-status={job.status} className={`border rounded-lg p-3 text-sm space-y-2 ${tone}`}>
      <div data-testid="restore-result-status" className="font-semibold">{label}</div>
      {job.error && <div data-testid="restore-result-error">{job.error}</div>}
      {errors.length > 0 && (
        <ul data-testid="restore-result-errors" className="list-disc pl-5">
          {errors.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
